import { useState } from 'react';
import ImageEditor from './ImageEditor';

function UploadModal({ user, onClose }) {
  const [originalImage, setOriginalImage] = useState('');
  const [editedImage, setEditedImage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Image Editor state
  const [showEditor, setShowEditor] = useState(false);

  const effectiveIndustry = user.current_industry || user.industry;
  
  // Handle save from Image Editor (receives all data including caption, hashtags)
  const handleEditorSave = (data) => {
    if (typeof data === 'string') {
      // Old format - just image
      setEditedImage(data);
    } else {
      // New format - object with image, caption, hashtags
      setEditedImage(data.image);
    }
    setShowEditor(false);
    setSuccess('✨ Image edited and saved!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );
    
    if (validFiles.length === 0) {
      setError('Please select valid image files (max 10MB)');
      return;
    }

    const file = validFiles[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      setOriginalImage(e.target.result);
      setEditedImage('');
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect({ target: { files } });
  };

  const displayImage = editedImage || originalImage;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="rounded-2xl max-w-3xl w-full p-6 max-h-[95vh] overflow-y-auto" 
           style={{ background: 'var(--bg-secondary)', border: '2px solid var(--accent-primary)', borderRadius: '16px' }}
           onClick={(e) => e.stopPropagation()}>
        
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold accent-text">📤 Upload & Edit Image</h3>
          <button onClick={onClose} className="text-3xl hover:opacity-70 transition" style={{ color: 'var(--text-secondary)' }}>×</button>
        </div>

        {/* File Upload Area */}
        {!originalImage && (
          <div 
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed rounded-xl p-12 text-center mb-6 hover:opacity-80 transition"
            style={{ borderColor: 'var(--accent-primary)', background: 'var(--bg-tertiary)' }}
          >
            <div className="text-6xl mb-4">📁</div>
            <p style={{ color: 'var(--text-secondary)' }} className="mb-2 text-lg">Drag and drop your image here</p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>or</p>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="file-input"
              data-testid="file-input"
            />
            <label 
              htmlFor="file-input" 
              className="px-8 py-3 rounded-lg font-medium cursor-pointer inline-block transition hover:opacity-90 text-lg"
              style={{ background: 'var(--accent-gradient)', color: 'white' }}
            >
              Choose Image
            </label>
            <p className="text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
              Supports JPG, PNG, WebP (max 10MB)
            </p>
          </div>
        )}

        {/* Image Preview & Editor CTA */}
        {originalImage && (
          <div className="mb-6">
            <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              {/* Image Preview */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {editedImage ? '✨ Edited Image' : '🖼️ Your Image'}
                  </h4>
                  <button
                    onClick={() => {
                      setOriginalImage('');
                      setEditedImage('');
                    }}
                    className="text-sm px-3 py-1 rounded-lg transition hover:bg-red-500/20"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    🗑️ Remove
                  </button>
                </div>
                <img 
                  src={displayImage} 
                  alt="Preview" 
                  className="w-full max-h-[350px] object-contain rounded-lg mx-auto" 
                />
              </div>

              {/* Open Editor Button - Primary CTA */}
              <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-primary)' }}>
                <button
                  onClick={() => setShowEditor(true)}
                  className="w-full py-4 rounded-xl font-semibold text-white transition hover:opacity-90 text-lg"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
                  data-testid="open-editor-btn"
                >
                  🎨 Open Real-Time Editor
                </button>
                <p className="text-center text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                  Add text, AI captions, logos, effects, social previews & more
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {displayImage && (
          <div className="flex gap-3">
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.download = `image-${Date.now()}.jpg`;
                link.href = displayImage;
                link.click();
              }}
              className="flex-1 py-3 rounded-xl font-medium transition hover:opacity-90"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
            >
              📥 Quick Download
            </button>
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="mt-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg text-sm">
            {success}
          </div>
        )}
      </div>
      
      {/* Image Editor Modal */}
      {showEditor && (
        <ImageEditor
          imageBase64={editedImage || originalImage}
          onSave={handleEditorSave}
          onClose={() => setShowEditor(false)}
          industry={effectiveIndustry}
        />
      )}
    </div>
  );
}

export default UploadModal;
